import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import { Upload, FileText, Download, Eye } from 'lucide-react';
import { Document, Task } from '../../../types';
import { DocumentUploadDialog } from './DocumentUploadDialog';

interface DocumentsTabProps {
  documents: Document[];
  tasks?: Task[];
  onDocumentUpload?: (document: Omit<Document, 'id' | 'uploadDate'>, taskId?: string) => void;
  onTaskComplete?: (taskId: string) => void;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  documents,
  tasks,
  onDocumentUpload,
  onTaskComplete,
}) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleUpload = (document: Omit<Document, 'id' | 'uploadDate'>, taskId?: string) => {
    onDocumentUpload?.(document, taskId);
    setUploadDialogOpen(false);
  };

  const getDocumentTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'legal':
        return 'primary';
      case 'medical':
        return 'success';
      case 'financial':
        return 'warning';
      case 'evidence':
        return 'error';
      default:
        return 'default';
    }
  };

  const DocumentCard = ({ document }: { document: Document }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>
              <FileText size={24} />
            </ListItemIcon>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                {document.title}
              </Typography>
              <Chip
                label={document.type}
                size="small"
                color={getDocumentTypeColor(document.type)}
              />
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Uploaded {new Date(document.uploadDate).toLocaleDateString()}
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            borderTop: 1,
            borderColor: 'divider',
            pt: 2,
            mt: 1,
          }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<Eye size={16} />}
              href={document.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View
            </Button>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<Download size={16} />}
              href={document.url}
              download
            >
              Download
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Paper>
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">Case Documents</Typography>
        <Button
          startIcon={<Upload size={18} />}
          variant="contained"
          onClick={() => setUploadDialogOpen(true)}
          fullWidth={isMobile}
        >
          Upload Document
        </Button>
      </Box>

      <Box sx={{ p: 2 }}>
        {isMobile ? (
          <Stack spacing={2}>
            {documents.map((document) => (
              <DocumentCard key={document.id} document={document} />
            ))}
            {documents.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No documents uploaded yet
              </Typography>
            )}
          </Stack>
        ) : (
          <List>
            {documents.map((document, index) => (
              <React.Fragment key={document.id}>
                <ListItem
                  sx={{
                    py: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon>
                    <FileText size={24} />
                  </ListItemIcon>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {document.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={document.type}
                        size="small"
                        color={getDocumentTypeColor(document.type)}
                      />
                      <Typography variant="caption" color="text.secondary" component="span">
                        •
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="span">
                        Uploaded {new Date(document.uploadDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      href={document.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Eye size={18} />
                    </IconButton>
                    <IconButton
                      size="small"
                      href={document.url}
                      download
                    >
                      <Download size={18} />
                    </IconButton>
                  </Box>
                </ListItem>
                {index < documents.length - 1 && <Divider />}
              </React.Fragment>
            ))}
            {documents.length === 0 && (
              <ListItem>
                <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    No documents uploaded yet
                  </Typography>
                </Box>
              </ListItem>
            )}
          </List>
        )}
      </Box>

      <DocumentUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleUpload}
        tasks={tasks}
        onTaskComplete={onTaskComplete}
        isMobile={isMobile}
      />
    </Paper>
  );
};